import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getSupabaseClient } from '../../lib/supabase';
import { Footer } from '@ui/components/Footer';
import { SeekerHeader } from '@ui/components/SeekerHeader';

export function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        const supabase = getSupabaseClient();
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('Auth session error:', sessionError);
          navigate('/login?error=auth_failed');
          return;
        }

        if (!session?.user) {
          console.error('No user session found');
          navigate('/login?error=no_session');
          return;
        }

        console.log('Auth session established for user:', session.user.id);

        // Check if profile exists for this user
        const { data: existingProfile, error: profileLookupError } = await supabase
          .from('profiles')
          .select('id, role, email')
          .eq('id', session.user.id)
          .maybeSingle();

        if (profileLookupError && profileLookupError.code !== 'PGRST116') {
          console.error('Error looking up profile:', profileLookupError);
          navigate('/login?error=profile_lookup_failed');
          return;
        }

        // If profile exists, email is confirmed — check if mid-onboarding or post-match
        if (existingProfile) {
          console.log('Profile already exists, email confirmed');
          localStorage.removeItem('pending_seeker_id');

          // Check if this seeker has been matched yet
          const { data: existingMatch } = await supabase
            .from('matches')
            .select('id')
            .eq('seeker_id', session.user.id)
            .eq('status', 'active')
            .limit(1)
            .maybeSingle();

          if (!existingMatch) {
            // No match yet — still mid-onboarding, send them back into the flow.
            // Works regardless of which browser/tab they clicked the link in.
            navigate('/onboarding/email-verification', { replace: true });
            return;
          }

          // Has a match — show the scheduling confirmation screen
          setConfirmed(true);
          return;
        }

        // Profile doesn't exist - need to provision the account
        console.log('No profile found, need to provision account');

        // Get the seeker ID from URL params or localStorage
        const seekerIdFromUrl = searchParams.get('link_seeker');
        const seekerIdFromStorage = localStorage.getItem('pending_seeker_id');
        const seekerId = seekerIdFromUrl || seekerIdFromStorage;

        if (!seekerId) {
          // No seeker ID from URL or localStorage — try to look up via email
          console.log('No seeker ID in URL/storage, looking up via email:', session.user.email);

          if (!session.user.email) {
            console.error('No email on session user');
            await supabase.auth.signOut();
            navigate('/');
            return;
          }

          try {
            const { data: emailCheck, error: emailCheckError } = await supabase.functions.invoke(
              'check-seeker-email',
              { body: { email: session.user.email } }
            );

            if (emailCheckError || !emailCheck) {
              console.error('Email lookup failed:', emailCheckError);
              await supabase.auth.signOut();
              navigate('/login?error=lookup_failed');
              return;
            }

            // Guide account — sign out and show error
            if (emailCheck.isGuide) {
              console.log('Guide account detected, signing out');
              await supabase.auth.signOut();
              navigate('/login?error=guide_account');
              return;
            }

            // Has onboarding data with a seeker_id — auto-provision
            if (emailCheck.onboardingStarted && emailCheck.seeker_id) {
              console.log('Found onboarding data, provisioning with seeker_id:', emailCheck.seeker_id);

              const { data: provisionData, error: provisionError } = await supabase.functions.invoke(
                'provision-seeker-account',
                {
                  body: {
                    seeker_id: emailCheck.seeker_id,
                    user_id: session.user.id,
                    email: session.user.email,
                    full_name: session.user.user_metadata?.full_name || session.user.user_metadata?.name || '',
                  }
                }
              );

              if (provisionError || provisionData?.error) {
                console.error('Provision error:', provisionError || provisionData?.error);
                await supabase.auth.signOut();
                navigate('/login?error=provision_failed');
                return;
              }

              console.log('Account provisioned successfully via email lookup');
              localStorage.removeItem('pending_seeker_id');

              // Check if matched — if not, still mid-onboarding
              const { data: matchAfterProvision } = await supabase
                .from('matches')
                .select('id')
                .eq('seeker_id', session.user.id)
                .eq('status', 'active')
                .limit(1)
                .maybeSingle();

              if (!matchAfterProvision) {
                navigate('/onboarding/email-verification', { replace: true });
                return;
              }
              setConfirmed(true);
              return;
            }

            // No onboarding data at all — nothing to provision, send to landing
            console.log('No onboarding data found for this email, redirecting to landing');
            await supabase.auth.signOut();
            navigate('/');
            return;
          } catch (lookupErr) {
            console.error('Email lookup threw:', lookupErr);
            await supabase.auth.signOut();
            navigate('/login?error=lookup_failed');
            return;
          }
        }

        console.log('Provisioning account for seeker:', seekerId);

        // Call the provision-seeker-account edge function
        const { data: provisionData, error: provisionError } = await supabase.functions.invoke(
          'provision-seeker-account',
          {
            body: {
              seeker_id: seekerId,
              user_id: session.user.id,
              email: session.user.email,
              full_name: session.user.user_metadata?.full_name || session.user.user_metadata?.name || '',
            }
          }
        );

        if (provisionError) {
          console.error('Error provisioning account:', provisionError);
          navigate('/login?error=provision_failed');
          return;
        }

        if (provisionData?.error) {
          console.error('Provision error from function:', provisionData.error);
          navigate('/login?error=provision_failed');
          return;
        }

        console.log('Account provisioned successfully');

        // Clean up storage
        localStorage.removeItem('pending_seeker_id');

        // Check if matched — if not, still mid-onboarding
        const { data: matchAfterDirectProvision } = await supabase
          .from('matches')
          .select('id')
          .eq('seeker_id', session.user.id)
          .eq('status', 'active')
          .limit(1)
          .maybeSingle();

        if (!matchAfterDirectProvision) {
          navigate('/onboarding/email-verification', { replace: true });
          return;
        }

        // Show confirmation screen
        setConfirmed(true);

      } catch (error) {
        console.error('Auth callback error:', error);
        navigate('/login?error=unexpected');
      }
    };

    handleAuthCallback();
  }, [navigate, searchParams]);

  if (confirmed) {
    return (
      <div className="flex min-h-screen w-full flex-col lg:overflow-auto overflow-hidden">
        <div className="relative flex flex-1 w-full flex-col bg-[#FFFCEF]">
          <header className="flex lg:hidden h-16 shrink-0 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
            <div className="w-10" aria-hidden />
            <div className="flex flex-1 justify-center">
              <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
            </div>
            <div className="w-10" aria-hidden />
          </header>
          <div className="hidden lg:block">
            <SeekerHeader />
          </div>

          <div className="flex flex-1 flex-col items-center lg:px-[120px] lg:py-16">
            <main className="flex flex-1 lg:flex-initial min-h-0 flex-col items-center justify-center px-6 lg:px-0 w-full max-w-[480px]">
              <div className="w-full rounded-2xl border border-[#304031]/15 bg-[#FFFCEF] p-8 text-center">
                <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#304031]/10">
                  <svg className="h-8 w-8 text-[#304031]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h1 className="mb-2 font-recoleta text-[28px] leading-[1.1] text-[#304031]">
                  Email Confirmed
                </h1>
                <p className="mb-6 text-sm leading-relaxed text-[#304031]/70">
                  Your account is all set. Now let's schedule your first consultation with your guide.
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/onboarding/match', { state: { openScheduler: true } })}
                  className="w-full rounded-full bg-[#304031] px-6 py-4 text-base font-semibold text-[#FEF6D6] transition hover:bg-[#304031]/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#304031]/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#FFFCEF]"
                >
                  Pick a Date & Time
                </button>
              </div>
            </main>
          </div>
        </div>

        <div className="hidden lg:block">
          <Footer />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full flex-col lg:overflow-auto overflow-hidden">
      <div className="relative flex flex-1 w-full flex-col bg-[#FFFCEF]">
        <header className="flex lg:hidden h-16 shrink-0 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
          <div className="w-10" aria-hidden />
          <div className="flex flex-1 justify-center">
            <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
          </div>
          <div className="w-10" aria-hidden />
        </header>
        <div className="hidden lg:block">
          <SeekerHeader />
        </div>

        <div className="flex flex-1 flex-col items-center lg:px-[120px] lg:py-16">
          <main className="flex flex-1 lg:flex-initial min-h-0 flex-col items-center justify-center px-6 lg:px-0 w-full max-w-[480px]">
            <div className="text-center">
              <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-[#304031]"></div>
              <p className="text-[#304031]/70">Completing your account setup...</p>
            </div>
          </main>
        </div>
      </div>

      <div className="hidden lg:block">
        <Footer />
      </div>
    </div>
  );
}
